import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, RadialBarChart, RadialBar, ComposedChart, ScatterChart, Scatter } from 'recharts';
import * as THREE from 'three';
import * as d3 from 'd3';
import { useAuth } from '../utils/auth';
import NotAuthorized from './NotAuthorized';

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historicalData, setHistoricalData] = useState([]);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [activeTab, setActiveTab] = useState('overview');
  const threeContainerRef = useRef(null);
  const networkContainerRef = useRef(null);
  const performanceGlobeRef = useRef(null);

  // Admin-only check
  if (user?.role !== 'admin') {
    return <NotAuthorized />;
  }

  // Simulate advanced metrics data
  const generateAdvancedMetrics = useCallback(() => {
    const now = Date.now();
    return {
      system: {
        cpu_percent: 45 + Math.random() * 30,
        memory_percent: 62 + Math.random() * 20,
        disk_usage: 78 + Math.random() * 10,
        network_io: Math.random() * 1000,
        cpu_temperature: 65 + Math.random() * 15,
        load_average: { '1min': 1.2 + Math.random() * 0.8 }
      },
      database: {
        connections: 45 + Math.floor(Math.random() * 20),
        active_connections: 12 + Math.floor(Math.random() * 8),
        total_users: 12847 + Math.floor(Math.random() * 100),
        query_time_avg: 15 + Math.random() * 10,
        slow_queries_count: Math.floor(Math.random() * 5),
        status: 'online'
      },
      application: {
        requests_per_minute: 234 + Math.floor(Math.random() * 100),
        response_time_avg: 145 + Math.random() * 50,
        response_time_p95: 280 + Math.random() * 100,
        error_rate: Math.random() * 0.05,
        active_sessions: 156 + Math.floor(Math.random() * 50)
      },
      performance: {
        health_score: 85 + Math.random() * 10,
        status: Math.random() > 0.8 ? 'warning' : 'healthy',
        alerts: Math.random() > 0.7 ? [{ type: 'warning', message: 'High CPU usage detected' }] : []
      },
      ai_agents: [
        { name: 'Design Specialist', load: 45 + Math.random() * 40, accuracy: 94.2, status: 'active' },
        { name: 'Performance Agent', load: 62 + Math.random() * 30, accuracy: 91.8, status: 'active' },
        { name: 'SEO Specialist', load: 38 + Math.random() * 35, accuracy: 96.1, status: 'active' },
        { name: 'Copy Analyst', load: 55 + Math.random() * 25, accuracy: 89.3, status: 'active' },
        { name: 'Business Intel', load: 71 + Math.random() * 20, accuracy: 92.7, status: 'active' }
      ],
      predictions: {
        cpu_forecast: Array.from({ length: 24 }, (_, i) => ({
          hour: i,
          predicted: 40 + Math.sin(i * Math.PI / 12) * 20 + Math.random() * 10,
          confidence: 0.8 + Math.random() * 0.15
        })),
        load_forecast: Array.from({ length: 7 }, (_, i) => ({
          day: i,
          expected_load: 200 + Math.sin(i * Math.PI / 3) * 100 + Math.random() * 50
        }))
      },
      geographic: [
        { country: 'US', users: 4234, load: 45, lat: 39.8283, lng: -98.5795 },
        { country: 'EU', users: 3876, load: 32, lat: 54.5260, lng: 15.2551 },
        { country: 'APAC', users: 2987, load: 67, lat: 35.8617, lng: 104.1954 },
        { country: 'SA', users: 987, load: 23, lat: -8.7832, lng: -55.4915 }
      ],
      timestamp: now
    };
  }, []);

  // Fetch metrics simulation
  const fetchMetrics = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      const data = generateAdvancedMetrics();
      
      setMetrics(data);
      setError(null);
      
      // Add to historical data
      const timestamp = new Date().toLocaleTimeString();
      setHistoricalData(prev => {
        const newData = [...prev, {
          time: timestamp,
          ...data.system,
          ...data.application,
          healthScore: data.performance.health_score,
          dbConnections: data.database.connections
        }];
        return newData.slice(-50); // Keep last 50 points
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [generateAdvancedMetrics]);

  // Auto-refresh effect
  useEffect(() => {
    fetchMetrics();
    
    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchMetrics, autoRefresh, refreshInterval]);

  // 3D System Health Visualization
  useEffect(() => {
    if (!threeContainerRef.current || !metrics) return;

    const container = threeContainerRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setClearColor(0x000000, 0);
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    // Create 3D health sphere
    const sphereGeometry = new THREE.SphereGeometry(2, 32, 32);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: metrics.performance.health_score > 80 ? 0x00ff88 : 
             metrics.performance.health_score > 60 ? 0xffaa00 : 0xff4444,
      wireframe: true
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    scene.add(sphere);

    // Add floating metrics cubes
    const cubeGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
    const cpuCube = new THREE.Mesh(cubeGeometry, new THREE.MeshBasicMaterial({ color: 0x3b82f6 }));
    const memoryCube = new THREE.Mesh(cubeGeometry, new THREE.MeshBasicMaterial({ color: 0x8b5cf6 }));
    const dbCube = new THREE.Mesh(cubeGeometry, new THREE.MeshBasicMaterial({ color: 0x06b6d4 }));
    
    cpuCube.position.set(-4, 0, 0);
    memoryCube.position.set(4, 0, 0);
    dbCube.position.set(0, 4, 0);
    
    scene.add(cpuCube, memoryCube, dbCube);

    camera.position.z = 8;

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      sphere.rotation.x += 0.01;
      sphere.rotation.y += 0.01;
      
      cpuCube.rotation.x += 0.02;
      memoryCube.rotation.y += 0.02;
      dbCube.rotation.z += 0.02;
      
      // Pulsing effect based on metrics
      const healthPulse = 1 + Math.sin(Date.now() * 0.005) * 0.1;
      sphere.scale.setScalar(healthPulse);
      
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      container.innerHTML = '';
    };
  }, [metrics]);

  // Network Topology Visualization
  useEffect(() => {
    if (!networkContainerRef.current || !metrics) return;

    const container = networkContainerRef.current;
    container.innerHTML = '';

    const width = container.clientWidth;
    const height = container.clientHeight;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    // Create network data
    const nodes = [
      { id: 'server', x: width/2, y: height/2, r: 30, color: '#3b82f6' },
      { id: 'db', x: width/4, y: height/3, r: 20, color: '#06b6d4' },
      { id: 'cache', x: 3*width/4, y: height/3, r: 15, color: '#8b5cf6' },
      { id: 'ai1', x: width/4, y: 2*height/3, r: 12, color: '#10b981' },
      { id: 'ai2', x: 3*width/4, y: 2*height/3, r: 12, color: '#f59e0b' }
    ];

    const links = [
      { source: 'server', target: 'db' },
      { source: 'server', target: 'cache' },
      { source: 'server', target: 'ai1' },
      { source: 'server', target: 'ai2' }
    ];

    // Draw connections
    svg.selectAll('.link')
      .data(links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('x1', d => nodes.find(n => n.id === d.source).x)
      .attr('y1', d => nodes.find(n => n.id === d.source).y)
      .attr('x2', d => nodes.find(n => n.id === d.target).x)
      .attr('y2', d => nodes.find(n => n.id === d.target).y)
      .attr('stroke', '#64748b')
      .attr('stroke-width', 2)
      .attr('opacity', 0.6);

    // Draw nodes
    svg.selectAll('.node')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('class', 'node')
      .attr('cx', d => d.x)
      .attr('cy', d => d.y)
      .attr('r', d => d.r)
      .attr('fill', d => d.color)
      .attr('opacity', 0.8)
      .style('filter', 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))');

    // Add pulsing animation
    svg.selectAll('.node')
      .transition()
      .duration(2000)
      .ease(d3.easeLinear)
      .attr('r', d => d.r * 1.2)
      .transition()
      .duration(2000)
      .attr('r', d => d.r)
      .on('end', function() {
        d3.select(this).transition().duration(0).attr('r', d => d.r);
      });

  }, [metrics]);

  // Animated Counter Component
  const AnimatedCounter = ({ value, suffix = '', duration = 1000 }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      let start = 0;
      const end = parseInt(value);
      if (start === end) return;

      let totalMilSecDur = duration;
      let incrementTime = (totalMilSecDur / end) * 1000;

      let timer = setInterval(() => {
        start += 1;
        setCount(String(start));
        if (start === end) clearInterval(timer);
      }, incrementTime);

      return () => clearInterval(timer);
    }, [value, duration]);

    return <span>{count}{suffix}</span>;
  };

  // Advanced Metric Card
  const AdvancedMetricCard = ({ title, value, unit, trend, color, icon, status }) => (
    <div className="relative group">
      {/* Glow effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${color} rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300`}></div>
      
      <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center`}>
              {icon}
            </div>
            <div>
              <h3 className="text-slate-300 font-medium text-sm">{title}</h3>
              {status && (
                <div className="flex items-center space-x-1 mt-1">
                  <div className={`w-2 h-2 rounded-full ${status === 'healthy' ? 'bg-green-400' : status === 'warning' ? 'bg-yellow-400' : 'bg-red-400'} animate-pulse`}></div>
                  <span className={`text-xs ${status === 'healthy' ? 'text-green-400' : status === 'warning' ? 'text-yellow-400' : 'text-red-400'}`}>
                    {status.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
          {trend && (
            <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
              <span className="mr-1">{trend > 0 ? '↗' : '↘'}</span>
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        
        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-bold text-white">
            <AnimatedCounter value={value} />
          </span>
          {unit && <span className="text-slate-400 text-lg">{unit}</span>}
        </div>
        
        {/* Animated progress bar */}
        <div className="mt-4 w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${color} transition-all duration-1000 ease-out`}
            style={{ width: `${Math.min(value, 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );

  // Custom Recharts components with enhanced styling
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 shadow-2xl">
          <p className="text-slate-300 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-white font-medium" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
        <div className="text-center">
          {/* Advanced loading animation */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-blue-400/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-blue-400 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-transparent border-t-purple-400 rounded-full animate-spin animation-delay-150"></div>
            <div className="absolute inset-4 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin animation-delay-300"></div>
          </div>
          
          <h3 className="text-3xl font-bold text-white mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Initializing Advanced Analytics
          </h3>
          <p className="text-slate-400 text-lg">Connecting to quantum-enhanced monitoring systems</p>
          <div className="mt-6 text-sm text-slate-500">
            🔒 Ultra-secure admin portal • Real-time AI insights
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'System Overview', icon: '🏠' },
    { id: 'performance', label: 'Performance', icon: '⚡' },
    { id: 'ai', label: 'AI Agents', icon: '🤖' },
    { id: 'predictive', label: 'Predictive', icon: '🔮' },
    { id: 'geographic', label: 'Geographic', icon: '🌍' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Futuristic Header */}
        <div className="relative mb-8">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 rounded-3xl blur-xl"></div>
          
          <div className="relative bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  🛡️ Quantum Analytics Command Center
                </h1>
                <p className="text-slate-400 text-xl">AI-powered system monitoring • Real-time intelligence • Predictive insights</p>
                <div className="flex items-center space-x-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium">All Systems Operational</span>
                  </div>
                  <div className="text-slate-500">•</div>
                  <div className="text-slate-400">Last updated: {new Date().toLocaleTimeString()}</div>
                </div>
              </div>
              
              {/* 3D Health Visualization */}
              <div className="hidden lg:block">
                <div ref={threeContainerRef} className="w-48 h-32 rounded-xl overflow-hidden bg-slate-800/30"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Tab Navigation */}
        <div className="mb-8">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-2">
            <div className="flex space-x-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="mb-8">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1h">Last Hour</option>
                  <option value="6h">Last 6 Hours</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                </select>
                
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                    autoRefresh
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                      : 'bg-slate-800/50 text-slate-400 hover:text-white'
                  }`}
                >
                  {autoRefresh ? '🔄 Auto Refresh' : '⏸️ Manual'}
                </button>
              </div>
              
              <button
                onClick={fetchMetrics}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 shadow-lg"
              >
                🔄 Refresh Now
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <AdvancedMetricCard
                title="System Health"
                value={Math.round(metrics?.performance?.health_score || 0)}
                unit="/100"
                color="from-green-500 to-emerald-500"
                icon={<span className="text-white text-lg">💚</span>}
                status={metrics?.performance?.status}
                trend={2.3}
              />
              <AdvancedMetricCard
                title="CPU Usage"
                value={Math.round(metrics?.system?.cpu_percent || 0)}
                unit="%"
                color="from-blue-500 to-cyan-500"
                icon={<span className="text-white text-lg">⚡</span>}
                trend={-1.2}
              />
              <AdvancedMetricCard
                title="Memory Usage"
                value={Math.round(metrics?.system?.memory_percent || 0)}
                unit="%"
                color="from-purple-500 to-pink-500"
                icon={<span className="text-white text-lg">🧠</span>}
                trend={0.8}
              />
              <AdvancedMetricCard
                title="Active Users"
                value={metrics?.database?.total_users || 0}
                color="from-orange-500 to-red-500"
                icon={<span className="text-white text-lg">👥</span>}
                trend={5.2}
              />
            </div>

            {/* Network Topology */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <span className="text-2xl mr-3">🕸️</span>
                Network Topology
              </h3>
              <div ref={networkContainerRef} className="w-full h-64 bg-slate-800/30 rounded-xl"></div>
            </div>

            {/* Real-time Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  System Performance
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={historicalData}>
                    <defs>
                      <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="cpu_percent" stroke="#3b82f6" fill="url(#cpuGradient)" strokeWidth={2} />
                    <Area type="monotone" dataKey="memory_percent" stroke="#8b5cf6" fill="url(#memoryGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <span className="text-2xl mr-3">⚡</span>
                  Response Times
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="response_time_avg" stroke="#10b981" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metrics?.ai_agents?.map((agent, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
                  
                  <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-bold">{agent.name}</h3>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-400 text-sm font-medium">ACTIVE</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-400">CPU Load</span>
                          <span className="text-white">{agent.load.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-700/50 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-1000"
                            style={{ width: `${agent.load}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-400">Accuracy</span>
                          <span className="text-white">{agent.accuracy}%</span>
                        </div>
                        <div className="w-full bg-slate-700/50 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-1000"
                            style={{ width: `${agent.accuracy}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'predictive' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <span className="text-2xl mr-3">🔮</span>
                  CPU Forecast (24h)
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={metrics?.predictions?.cpu_forecast}>
                    <defs>
                      <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="hour" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="predicted" stroke="#f59e0b" fill="url(#forecastGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <span className="text-2xl mr-3">📈</span>
                  Load Prediction (7d)
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={metrics?.predictions?.load_forecast}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="day" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="expected_load" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;